import React, { useState, useEffect, useRef } from 'react';
import { FaTrash, FaFileInvoiceDollar, FaFileUpload, FaBook } from 'react-icons/fa';
import Swal from 'sweetalert2';
import retencionHonorariosService from '../../services/retencionHonorariosService';
import YearSelector from '../../components/common/YearSelector';
import useYearSelector from '../../hooks/useYearSelector';
import '../../styles/pages/RRHH/RetencionHonorariosPage.css';

const MESES = [
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
];

const ESTADOS = [
    { value: 'pendiente', label: 'Pendiente', color: 'warning' },
    { value: 'pagado', label: 'Pagado', color: 'success' },
    { value: 'anulado', label: 'Anulado', color: 'danger' }
];

/**
 * Página de Retención de Honorarios
 * Gestión de boletas de honorarios de profesionales terceros
 */
const RetencionHonorariosPage = ({ token, id_company }) => {
    const [retenciones, setRetenciones] = useState([]);
    const [profesionales, setProfesionales] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(true);

    const fileInputRef = useRef(null);

    const { selectedYear, setSelectedYear, yearOptions } = useYearSelector(null, 10, 1);
    const [selectedMes, setSelectedMes] = useState('');

    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroProfesional, setFiltroProfesional] = useState('');

    useEffect(() => {
        fetchProfesionales();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedYear) {
            fetchRetenciones();
            fetchResumen();
        }
    }, [filtroEstado, filtroProfesional, selectedYear, selectedMes]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchRetenciones = async () => {
        try {
            setLoading(true);
            const params = { id_company, anio: selectedYear };
            if (selectedMes) params.mes = selectedMes;
            if (filtroEstado) params.estado = filtroEstado;
            if (filtroProfesional) params.rut_profesional = filtroProfesional;
            const data = await retencionHonorariosService.getRetencionesHonorarios(params, token);
            setRetenciones(data);
        } catch (error) {
            console.error('Error al cargar retenciones:', error);
            Swal.fire('Error', 'No se pudieron cargar las retenciones', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchResumen = async () => {
        try {
            const data = await retencionHonorariosService.getResumen(
                selectedYear, selectedMes || null, id_company, token
            );
            setResumen(data);
        } catch (error) {
            console.error('Error al cargar resumen:', error);
        }
    };

    const fetchProfesionales = async () => {
        try {
            const data = await retencionHonorariosService.getProfesionales(id_company, token);
            setProfesionales(data);
        } catch (error) {
            console.error('Error al cargar profesionales:', error);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await retencionHonorariosService.deleteRetencionHonorario(id, id_company, token);
                Swal.fire('Eliminado', 'Retención eliminada correctamente', 'success');
                fetchRetenciones();
                fetchResumen();
                fetchProfesionales();
            } catch (error) {
                console.error('Error al eliminar:', error);
                Swal.fire('Error', error.message || 'Error al eliminar retención', 'error');
            }
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validExtensions = ['.xls', '.xlsx'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            Swal.fire('Error', 'Por favor seleccione un archivo de Excel (.xls o .xlsx)', 'error');
            event.target.value = '';
            return;
        }

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('id_company', id_company);

            Swal.fire({
                title: 'Procesando archivo...',
                text: 'Por favor espere mientras se cargan los honorarios',
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); }
            });

            await retencionHonorariosService.uploadHonorariosMensuales(formData, token);

            Swal.fire('Éxito', 'Honorarios mensuales cargados correctamente', 'success');
            fetchRetenciones();
            fetchResumen();
            fetchProfesionales();
        } catch (error) {
            console.error('Error al cargar archivo:', error);
            Swal.fire('Error', error.message || 'Error al cargar el archivo de honorarios', 'error');
        } finally {
            event.target.value = '';
        }
    };

    const handleUploadButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleCrearAsientos = async () => {
        if (!selectedMes) {
            Swal.fire('Atención', 'Debe seleccionar un mes para generar los asientos contables', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: 'Crear Asientos Contables',
            html: `
                <p>Se crearán los asientos contables para todas las retenciones de honorarios de <strong>${MESES.find(m => m.value === selectedMes)?.label} ${selectedYear}</strong> que aún no tengan asiento generado.</p>
                <br/>
                <p><strong>Asientos a generar:</strong></p>
                <ul style="text-align: left; margin-top: 10px;">
                    <li>DEBE: Gasto en Honorarios</li>
                    <li>HABER: Retención Honorarios por Pagar</li>
                    <li>HABER: Honorarios por Pagar</li>
                </ul>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Crear Asientos',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Procesando...',
                    text: 'Creando asientos contables',
                    allowOutsideClick: false,
                    didOpen: () => { Swal.showLoading(); }
                });

                const response = await retencionHonorariosService.crearAsientosContables({
                    id_company,
                    mes: parseInt(selectedMes),
                    anio: selectedYear
                }, token);

                Swal.fire({
                    title: 'Asientos Creados',
                    html: `
                        <p>Se procesaron <strong>${response.data.retenciones_procesadas}</strong> retenciones.</p>
                        <br/>
                        <table style="width: 100%; text-align: left; font-size: 14px;">
                            <tr><td>Monto Bruto:</td><td style="text-align: right;">${formatCurrency(response.data.totales.monto_bruto)}</td></tr>
                            <tr><td>Retención:</td><td style="text-align: right;">${formatCurrency(response.data.totales.monto_retencion)}</td></tr>
                            <tr><td>Monto Líquido:</td><td style="text-align: right;">${formatCurrency(response.data.totales.monto_liquido)}</td></tr>
                        </table>
                    `,
                    icon: 'success'
                });

                fetchRetenciones();
            } catch (error) {
                console.error('Error al crear asientos:', error);
                Swal.fire('Error', error.message || 'Error al crear asientos contables', 'error');
            }
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(value);
    };

    return (
        <div className="retencion-honorarios-page">
            <div className="page-header">
                <div className="header-left">
                    <FaFileInvoiceDollar className="page-icon" />
                    <div>
                        <h1>Retención de Honorarios</h1>
                        <p>Gestión de boletas de profesionales terceros</p>
                    </div>
                </div>
                <div className="header-right">
                    <YearSelector
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        yearOptions={yearOptions}
                        label="Año"
                        showIcon={true}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <button className="btn-primary" onClick={handleUploadButtonClick}>
                        <FaFileUpload /> Cargar Honorarios Mensuales
                    </button>
                    <button className="btn-secondary" onClick={handleCrearAsientos} title="Crear asientos contables del mes seleccionado">
                        <FaBook /> Crear Asientos Contables
                    </button>
                </div>
            </div>

            {/* Resumen */}
            {resumen && (
                <div className="resumen-cards">
                    <div className="resumen-card">
                        <h3>Total Retenciones</h3>
                        <p className="value">{resumen.total_retenciones}</p>
                    </div>
                    <div className="resumen-card warning">
                        <h3>Pendientes</h3>
                        <p className="value">{resumen.pendientes}</p>
                        <p className="sub-value">{formatCurrency(resumen.monto_pendiente)}</p>
                    </div>
                    <div className="resumen-card success">
                        <h3>Pagadas</h3>
                        <p className="value">{resumen.pagadas}</p>
                        <p className="sub-value">{formatCurrency(resumen.monto_pagado)}</p>
                    </div>
                    <div className="resumen-card">
                        <h3>Total Honorarios</h3>
                        <p className="value">{formatCurrency(resumen.total_monto_bruto)}</p>
                    </div>
                    <div className="resumen-card">
                        <h3>Total Retenciones</h3>
                        <p className="value">{formatCurrency(resumen.total_retenciones_monto)}</p>
                    </div>
                </div>
            )}

            {/* Filtros */}
            <div className="filters">
                <div className="filter-group">
                    <label>Mes:</label>
                    <select value={selectedMes} onChange={(e) => setSelectedMes(e.target.value)}>
                        <option value="">Todos</option>
                        {MESES.map(mes => (
                            <option key={mes.value} value={mes.value}>{mes.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Estado:</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        {ESTADOS.map(estado => (
                            <option key={estado.value} value={estado.value}>{estado.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Profesional:</label>
                    <select value={filtroProfesional} onChange={(e) => setFiltroProfesional(e.target.value)}>
                        <option value="">Todos</option>
                        {profesionales.map(prof => (
                            <option key={prof.rut_profesional} value={prof.rut_profesional}>
                                {prof.nombre_profesional}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabla de retenciones */}
            <div className="table-container">
                {loading ? (
                    <div className="loading">Cargando retenciones...</div>
                ) : retenciones.length === 0 ? (
                    <div className="empty-state">
                        <FaFileInvoiceDollar size={48} />
                        <p>No hay retenciones registradas para este filtro</p>
                    </div>
                ) : (
                    <table className="retencion-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Profesional</th>
                                <th>N° Boleta</th>
                                <th>Monto Bruto</th>
                                <th>Retención</th>
                                <th>Monto Líquido</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {retenciones.map(retencion => (
                                <tr key={retencion.id_retencion}>
                                    <td>{new Date(retencion.fecha_emision).toLocaleDateString('es-CL')}</td>
                                    <td>
                                        {retencion.nombre_profesional}
                                        <div className="sub-text">{retencion.rut_profesional}</div>
                                    </td>
                                    <td>{retencion.numero_boleta || '-'}</td>
                                    <td className="currency">{formatCurrency(retencion.monto_bruto)}</td>
                                    <td className="currency">
                                        {formatCurrency(retencion.monto_retencion)}
                                        <div className="sub-text">{retencion.tasa_retencion}%</div>
                                    </td>
                                    <td className="currency">{formatCurrency(retencion.monto_liquido)}</td>
                                    <td>
                                        <span className={`badge badge-${ESTADOS.find(e => e.value === retencion.estado_pago)?.color}`}>
                                            {retencion.estado_pago}
                                        </span>
                                        {retencion.fecha_pago && (
                                            <div className="sub-text">
                                                Pagado: {new Date(retencion.fecha_pago).toLocaleDateString('es-CL')}
                                            </div>
                                        )}
                                    </td>
                                    <td className="actions">
                                        <button
                                            className="btn-icon btn-delete"
                                            onClick={() => handleDelete(retencion.id_retencion)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default RetencionHonorariosPage;
